import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import SemanticInterlinker from '../components/SemanticInterlinker';
import { Suspense } from 'react';

const Highlights = React.lazy(() => import('../components/Highlights'));
import './Home.css';

export default function Home() {
  const [bulletins, setBulletins] = useState([]);
  const [olympiads, setOlympiads] = useState([]);
  const [loadingBulletins, setLoadingBulletins] = useState(true);
  const [loadingOlympiads, setLoadingOlympiads] = useState(true);

  useEffect(() => {
    fetch('/api/cms/bulletins')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const activeBulletins = data.filter(b => b.is_active);
          // Prepend the static MSO 2027 Notification
          activeBulletins.unshift({
            id: 'mso-live-2027',
            type: 'REGISTRATION OPEN',
            title: '🚀 Mathura Science Olympiad (MSO) 2027 is LIVE!',
            description: 'Registrations for the 1st Edition of MSO are now open for Classes 7 to 12.  Last Date of Registration is 30 Nov 2026.',
            link: '/olympiads?exam=mso'
          });
          setBulletins(activeBulletins);
        }
      })
      .catch(console.error)
      .finally(() => setLoadingBulletins(false));

    fetch('/api/cms/olympiads')
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setOlympiads(data);
        }
      })
      .catch(console.error)
      .finally(() => setLoadingOlympiads(false));
  }, []);

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Dynamic Exams",
    "url": "https://dynamicexams.org",
    "logo": "https://dynamicexams.org/logo.png",
    "description": "Join the largest Olympiad platform in Mathura. Discover your potential and compete with the best.",
    "sameAs": [
      "https://www.facebook.com/dynamicexams",
      "https://www.instagram.com/dynamicexams"
    ]
  };

  return (
    <div className="home-page">
      <Helmet>
        <title>Dynamic Exams | Empowering Minds Through Excellence</title>
        <meta name="description" content="Join the largest Olympiad platform in Mathura. Discover your potential and compete with the best in Mathematics, Science, and Cyber Olympiads." />
        <link rel="preload" as="image" href="/olympiads-banner.webp" type="image/webp" fetchpriority="high" />
        <meta property="og:title" content="Dynamic Exams | Empowering Minds" />
        <meta property="og:description" content="Join the largest Olympiad platform in Mathura. Discover your potential and compete with the best." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dynamicexams.org/" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      {/* Banner Section (Full Width) */}
      <section className="banner-section" style={{ width: '100%', background: '#020611' }}>
          <img
            src="/olympiads-banner.webp"
            alt="Olympiads Banner"
            style={{
              width: '100%',
              height: 'auto',
              display: 'block',
              aspectRatio: '2/1'
            }}
            loading="eager"
            fetchPriority="high"
            decoding="async"
            width="1774"
            height="887"
          />
      </section>

      {/* Hero Section */}
      <section className="hero">
        <div className="container hero-content">
          <h1 className="hero-title">
            Empowering Minds Through <span className="text-highlight">Excellence</span>
          </h1>
          <p className="hero-subtitle">
            <SemanticInterlinker>
            Join the largest Olympiad platform in Mathura.<br />
            Discover your potential and compete with the best.
            </SemanticInterlinker>
          </p>
          <div className="hero-cta">
            <Link to="/olympiads" className="btn btn-primary">View Olympiads</Link>
          </div>
        </div>
        <div className="hero-bg-shape"></div>
      </section>



      {/* 📢 DYNAMIC BULLETIN BOARD (Information Centre) */}
      <section className="section-padding" style={{ backgroundColor: 'rgba(30, 58, 95, 0.2)' }}>
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Information Bulletin Centre</h2>
            <div className="bg-red-500/20 text-red-400 font-bold px-4 py-2 rounded-full border border-red-500/30 flex items-center gap-2">
              <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-pulse"></span>
              LIVE UPDATES
            </div>
          </div>

          {loadingBulletins ? (
            <div className="bulletin-grid-placeholder" style={{ minHeight: '180px' }}></div>
          ) : bulletins.length > 0 ? (
            <div className="bulletin-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem', minHeight: '180px' }}>
              {bulletins.map(b => (
                <div key={b.id} className="glass-panel" style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', gap: '1rem', borderLeft: '4px solid var(--secondary-gold)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', fontWeight: 'bold', color: 'var(--secondary-gold)', textTransform: 'uppercase', letterSpacing: '1px', background: 'rgba(240,180,41,0.1)', padding: '0.2rem 0.5rem', borderRadius: '4px' }}>
                      {b.type}
                    </span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>New</span>
                  </div>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', lineHeight: '1.5', color: '#ff4444', fontWeight: 'bold' }}>{b.title}</h3>
                  {b.description && (
                    <p style={{ margin: 0, fontSize: '0.95rem', color: 'var(--text-primary)', fontWeight: 'normal', lineHeight: '1.4' }}>{b.description}</p>
                  )}
                  {b.link && (
                    b.link.startsWith('/') ? (
                      <Link to={b.link} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold', marginTop: 'auto' }}>
                        Read More <ArrowRight size={14} />
                      </Link>
                    ) : (
                      <a href={b.link.startsWith('http') ? b.link : `https://${b.link}`} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontWeight: 'bold', marginTop: 'auto' }}>
                        Read More <ArrowRight size={14} />
                      </a>
                    )
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="glass-panel" style={{ padding: '3rem', textAlign: 'center', color: 'var(--text-secondary)', border: '1px dashed rgba(255,255,255,0.2)' }}>
              <h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)', fontSize: '1.2rem', fontWeight: 'bold' }}>No Active Updates</h3>
              <p>Check back later for important announcements, exam notifications, and results.</p>
            </div>
          )}
        </div>
      </section>

      <Suspense fallback={<div style={{ minHeight: '300px' }}></div>}>
        <Highlights />
      </Suspense>

      {/* Olympiads Preview */}
      <section className="olympiads-preview section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Olympiads</h2>
            <Link to="/olympiads" className="view-all-link">
              View All <ArrowRight size={16} />
            </Link>
          </div>

          {loadingOlympiads ? (
            <div className="olympiads-grid-placeholder" style={{ minHeight: '200px' }}></div>
          ) : (
            <div className="olympiads-grid" style={{ minHeight: '200px' }}>
              {olympiads.map((oly, idx) => {
              const match = oly.name.match(/(.+)\s\((.+)\)/);
              const acronym = match ? match[2] : oly.name.substring(0, 4);
              const fullName = match ? match[1] : oly.name;
              return (
                <div key={idx} className="oly-card">
                  <div className="oly-card-icon" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'transparent'
                  }}>
                    <picture>
                      <img
                        src={oly.logo_data ? oly.logo_data : `/logos/${acronym.toLowerCase()}.webp`}
                        alt={`${fullName} Logo`}
                        loading="lazy"
                        decoding="async"
                        width="400"
                        height="400"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          aspectRatio: '1 / 1'
                        }}
                      />
                    </picture>
                  </div>
                  <div className="oly-card-content">
                    <h3 className="oly-card-title">{fullName}</h3>
                    <Link to="/olympiads" className="oly-card-link">Learn more <span className="sr-only">about {acronym}</span></Link>
                  </div>
                </div>
              );
            })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
